/**
 * Client-side garment image quality scoring using MobileNetV3-Small via ONNX Runtime Web.
 *
 * Model: almogtavor/vestir-garment-quality (INT8)
 * Outputs:
 *   - quality: [0-1] score (1 = best quality)
 *   - embedding: 128-dim L2-normalized feature vector
 *
 * Use to auto-select the best image when multiple are available,
 * and warn users about low-quality uploads.
 */

import * as ort from 'onnxruntime-web';
import { fetchHFModel } from './hfModelLoader';

const MODEL_URL =
  'https://huggingface.co/almogtavor/vestir-garment-quality/resolve/main/model_int8.onnx';
const IMG_SIZE = 224;
const LOW_QUALITY_THRESHOLD = 0.35;

// ImageNet normalization
const MEAN = [0.485, 0.456, 0.406];
const STD = [0.229, 0.224, 0.225];

let _session: ort.InferenceSession | null = null;
let _sessionLoading: Promise<ort.InferenceSession> | null = null;

async function getSession(): Promise<ort.InferenceSession> {
  if (_session) return _session;
  if (_sessionLoading) return _sessionLoading;

  _sessionLoading = (async () => {
    try {
      const modelBuffer = await fetchHFModel(MODEL_URL);
      const session = await ort.InferenceSession.create(modelBuffer, {
        executionProviders: ['wasm'],
      });
      _session = session;
      return session;
    } catch (err) {
      _sessionLoading = null;
      throw err;
    }
  })();

  return _sessionLoading;
}

/** Pre-load the model. */
export async function warmUpQuality(): Promise<void> {
  await getSession();
}

function imageToTensor(imageData: ImageData): ort.Tensor {
  const { data } = imageData;
  const float32 = new Float32Array(3 * IMG_SIZE * IMG_SIZE);

  for (let y = 0; y < IMG_SIZE; y++) {
    for (let x = 0; x < IMG_SIZE; x++) {
      const srcIdx = (y * IMG_SIZE + x) * 4;
      const dstIdx = y * IMG_SIZE + x;

      float32[0 * IMG_SIZE * IMG_SIZE + dstIdx] = (data[srcIdx] / 255 - MEAN[0]) / STD[0];
      float32[1 * IMG_SIZE * IMG_SIZE + dstIdx] = (data[srcIdx + 1] / 255 - MEAN[1]) / STD[1];
      float32[2 * IMG_SIZE * IMG_SIZE + dstIdx] = (data[srcIdx + 2] / 255 - MEAN[2]) / STD[2];
    }
  }

  return new ort.Tensor('float32', float32, [1, 3, IMG_SIZE, IMG_SIZE]);
}

async function loadImageData(src: string | HTMLImageElement): Promise<ImageData> {
  let img: HTMLImageElement;
  if (typeof src === 'string') {
    img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load image'));
      el.src = src;
    });
  } else {
    img = src;
  }

  const canvas = document.createElement('canvas');
  canvas.width = IMG_SIZE;
  canvas.height = IMG_SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, IMG_SIZE, IMG_SIZE);
  return ctx.getImageData(0, 0, IMG_SIZE, IMG_SIZE);
}

/** Get quality score [0-1] for a garment image. Returns 0 if model fails. */
export async function getQualityScore(
  imageSource: string | HTMLImageElement,
): Promise<number> {
  try {
    const session = await getSession();
    const imageData = await loadImageData(imageSource);
    const tensor = imageToTensor(imageData);
    const results = await session.run({ pixel_values: tensor });
    return (results.quality.data as Float32Array)[0];
  } catch {
    return 0;
  }
}

/** Check if an image is below the quality threshold. */
export function isLowQuality(score: number): boolean {
  return score < LOW_QUALITY_THRESHOLD;
}

export { LOW_QUALITY_THRESHOLD };
